<template>
	<div class="time-entry-actions-input" :class="classAddons">
		<SuiTimeInput
			ref="$timeEntryInput"
			v-model="timeEntryValue"
			class="time-entry-actions-input__input"
			size="small"
			:align="SuiInputAlign.Center"
			full-width
			hide-clear-button
			:hours-per-day="hoursPerDay"
			@submit="onTimeEntrySave"
			@focus="onFocus"
			@keydown.esc="$emit('close')"
			@focusout="onFocusOut"
		/>

		<SuiButton
			v-tooltip="$t('modifyTimeEntry')"
			class="time-entry-actions-input__modify"
			:aria-label="$t('modifyTimeEntry')"
			variant="icon"
			tabindex="-1"
			@focus="onFocus"
			@click="onModifyTimeEntryClick()"
		>
			<SuiIcon name="icon-link-out-16" />
		</SuiButton>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import {
	SuiTimeInput,
	SuiButton,
	SuiIcon,
	SuiInputAlign,
} from '@developers/scoro-ui';
import { type ValueOf } from '@/types';
import { timeToSeconds } from '@/utils/time';
import { wait } from '@/utils/misc';
import { TIME_ENTRY_STATE } from '../types';
import { TIME_ENTRY_ANIMATION_DURATION } from '../../constants';
import { useSettings } from '@/composables';
import { Setting } from '@/constants/settings';

export type TimeEntryInputProps = {
	defaultTimeEntryValue: string;
};

const props = defineProps<TimeEntryInputProps>();

const emit = defineEmits<{
	(e: 'add', duration: number): void;
	(e: 'close'): void;
	(e: 'blur', duration: number): void;
	(e: 'modify', duration: number): void;
}>();

const { setting } = useSettings();

const hoursPerDay = computed(() =>
	Number(setting(Setting.SYSTEM_DAY_UNIT_SUB_VALUE))
);

const $timeEntryInput = ref<typeof SuiTimeInput>();
const $input = computed<HTMLInputElement | undefined>(
	() => $timeEntryInput.value?.$refs.$suiInput.$refs.input
);

const timeEntryValue = ref(props.defaultTimeEntryValue);
const timeEntryState = ref<ValueOf<typeof TIME_ENTRY_STATE>>();
let timeoutId: ReturnType<typeof setTimeout>;

const focusAndSelectAll = async ($input?: HTMLInputElement) => {
	if ($input) {
		$input.focus();

		await nextTick();
		$input.select();
	}
};

const onModifyTimeEntryClick = () => {
	emit('close');
	emit('modify', timeToSeconds(timeEntryValue.value));
};

const onBlur = async () => {
	const duration = timeToSeconds(timeEntryValue.value);

	if (timeEntryState.value !== TIME_ENTRY_STATE.ERROR) {
		if (duration) {
			timeEntryState.value = TIME_ENTRY_STATE.SUCCESS;
			emit('add', duration);

			timeoutId = setTimeout(() => {
				timeEntryState.value = undefined;
				emit('blur', duration);
			}, 300);
		} else {
			emit('close');
		}
	} else {
		timeEntryState.value = TIME_ENTRY_STATE.ERROR;

		await wait(150);
		await focusAndSelectAll($input.value);
		await wait(TIME_ENTRY_ANIMATION_DURATION);
		timeEntryState.value = undefined;
	}
};

const onFocusOut = (e: MouseEvent) => {
	// need to check the relatedTarget, because the blur event was fired when the user clicked on "open modal" button
	// if the modal is opened, we don't fire the onBlur() method, but let the onClick execute instead
	const relatedTarget = e.relatedTarget;

	if (
		!(relatedTarget instanceof Element) ||
		!relatedTarget.classList.contains('time-entry-actions-input__modify')
	) {
		onBlur();
	}

	if (
		relatedTarget instanceof Element &&
		relatedTarget.classList.contains('time-entry-done__button')
	) {
		emit('blur', timeToSeconds(timeEntryValue.value));
	}
};

const onFocus = () => {
	clearTimeout(timeoutId);
};

const onTimeEntrySave = async () => {
	const duration = timeToSeconds(timeEntryValue.value);

	if (duration) {
		timeEntryState.value = TIME_ENTRY_STATE.SUCCESS;
		emit('add', duration);

		setTimeout(() => {
			timeEntryState.value = undefined;
			emit('close');
		}, 300);
	} else {
		$input.value?.blur();
	}
};

const classAddons = computed(() => {
	return {
		[`time-entry-actions-input--${timeEntryState.value}`]:
			timeEntryState.value,
	};
});

onMounted(() => {
	if ($input.value) {
		focusAndSelectAll($input.value);
	}
});
</script>

<style lang="scss">
.time-entry-actions-input {
	$parent: &;

	position: relative;

	&__modify.time-entry-actions-input__modify {
		--sui-button-background-color: #{$blue-10};
		--sui-button-hover-background-color: #{$blue-20};
		--sui-button-v-padding: 0.5rem;
		--sui-button-h-padding: 0.5rem;
		--sui-button-icon-color: #{$blue-50};
		--sui-button-hover-icon-color: #{$blue-60};

		position: absolute;
		top: -1rem;
		right: -1rem;
		z-index: 2;
		border-radius: 2rem;
	}

	&--success {
		animation: success 0.2s 0.6s ease forwards;

		#{$parent}__input {
			.sui-input {
				--sui-input-color-text: #{$green-80};

				background: $green-10;
				border-color: $green-30;
			}
		}
	}

	&--error {
		#{$parent}__input {
			.sui-input {
				animation: error 0.8s ease forwards;
			}
		}
	}

	@keyframes success {
		0% {
			opacity: 1;
			transform: translateY(0);
		}

		100% {
			opacity: 0;
			transform: translateY(1rem);
		}
	}

	@keyframes error {
		30% {
			border-color: $red-40;
		}

		50% {
			border-color: $red-20;
		}

		80% {
			border-color: $red-50;
		}
	}
}
</style>
